import './MainLayout.scss';
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';

import TopBar from './TopBar';
import { useAuth } from '../session/InternalAuthProvider';
import { Currency } from '../api';
import MainSidebar, { mainSidebarRailWidth, mainSidebarWidth } from './sideBar/MainSidebar';

/** Remembered per device: a desk that folded the menu away wants it to stay away. */
const SIDEBAR_COLLAPSED_KEY = 'webook-sidebar-collapsed';

const MainLayout = () => {
  const location = useLocation();
  const isCalendarRoute = location.pathname.includes('/dashboard/calendar');

  const { authState } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Desktop only: the permanent sidebar folds away to give the content its 260px. Phones use the
  // temporary drawer (isSidebarOpen) and ignore this.
  const [isSidebarCollapsed, setIsSidebarCollapsedState] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
  );
  const setIsSidebarCollapsed = (collapsed) => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    setIsSidebarCollapsedState(collapsed);
  };
  const contextValue = {
    isSidebarOpen,
    setIsSidebarOpen,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  };
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  useEffect(() => {
    // US accounts have no language toggle, so they always load in English
    const isUsAccount = authState.account.currency === Currency.USD;
    const defaultLang = isUsAccount ? 'en' : authState.account.default_system_language || 'he';
    const localStorageLanguage = localStorage.getItem('calendar_language');
    if (!isUsAccount && localStorageLanguage && localStorageLanguage !== 'undefined') {
      return;
    }
    void i18n.changeLanguage(defaultLang);
    document.body.dir = i18n.dir();
    theme.direction = i18n.dir();
    moment.locale(defaultLang);
    localStorage.setItem('calendar_language', defaultLang);
  }, [authState.account]);
  return (
    <Grid
      container
      sx={{
        // dvh, not vh: on a phone 100vh is the viewport *without* the browser's retractable
        // address bar, so a vh-sized shell hangs ~60-100px past what is actually on screen. With
        // the shell clipped and the body unable to scroll, that strip is lost for good.
        minHeight: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
      }}>
      <Grid
        item
        sx={{
          width: { xs: 0, md: isSidebarCollapsed ? mainSidebarRailWidth : mainSidebarWidth },
          display: { xs: 'none', md: 'block' },
          bgcolor: 'mb.side',
        }}>
        <MainSidebar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          isCollapsed={isSidebarCollapsed}
          onCollapse={() => setIsSidebarCollapsed(true)}
          onExpand={() => setIsSidebarCollapsed(false)}
        />
      </Grid>

      <Grid
        item
        xs
        container
        flexDirection="column"
        sx={{
          height: '100dvh',
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}>
        {!isCalendarRoute && isSmallScreen && (
          <Grid item>
            <TopBar fixed isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} className="elevation-z8" />
          </Grid>
        )}
        <Grid
          item
          xs
          sx={{
            overflowY: isCalendarRoute ? 'hidden' : 'auto',
            height: '100%',
          }}>
          <Outlet context={contextValue} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MainLayout;
